import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { getRoleHomeRoute } from '@/lib/role-redirect';
import type { MeProfile, UserRole } from '@/types';
import {
  getMe,
  login,
  type LoginSuccessResponse,
  setActivePropertyId,
} from '@/services/api.client';

const LOCKOUT_SECONDS = 15 * 60;

const loginSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [secondsLeft, setSecondsLeft] = useState(LOCKOUT_SECONDS);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const mutation = useMutation({
    mutationFn: ({ email, password }: LoginFormValues) => login(email, password),
    onSuccess: async (data) => {
      if ('mfaPending' in data && data.mfaPending) {
        // Record when the challenge was issued so MfaPage can initialize its
        // countdown from the actual elapsed time rather than a full 5 minutes.
        sessionStorage.setItem('secure-gate:mfa-challenge-at', String(Date.now()));
        void navigate('/mfa');
        return;
      }
      if ('mfaSetupRequired' in data) {
        void navigate('/mfa-setup');
        return;
      }

      // After MFA checks, data is a full login success response.
      const session = data as LoginSuccessResponse;

      // Set X-Property-Id from the login response so subsequent API calls
      // (including getMe) have tenant context on plain localhost.
      const loginPropertyId = session.propertyRoles?.[0]?.propertyId ?? null;
      if (loginPropertyId) {
        setActivePropertyId(loginPropertyId);
      }

      // Build a minimal MeProfile from the login response and seed the
      // query cache immediately. This guarantees AuthProvider sees an
      // authenticated user on the very next render — no race with a
      // network fetch that might not have the cookie ready yet.
      const now = new Date().toISOString();
      const seedProfile: MeProfile = {
        id: session.userId,
        email: session.email,
        fullName: session.fullName,
        phone: null,
        platformRole: session.platformRole as MeProfile['platformRole'],
        propertyRoles: session.propertyRoles.map((r) => ({
          propertyId: r.propertyId,
          role: r.role as UserRole,
          subdomain: r.subdomain ?? null,
        })),
        status: 'active',
        emailVerified: true,
        lastLoginAt: now,
        createdAt: now,
        updatedAt: now,
      };
      queryClient.setQueryData(['me'], seedProfile);

      // Navigate to the role-appropriate dashboard.
      const route = getRoleHomeRoute(seedProfile);

      // If the user belongs to an estate with a subdomain and we're on the
      // platform alias (app.*), redirect them to their estate's subdomain.
      const host = window.location.hostname;
      const platformAliases = ['app', 'www', 'admin'];
      const hostParts = host.split('.');
      const firstPart = hostParts[0] ?? '';
      const isOnPlatformAlias = hostParts.length >= 3 && platformAliases.includes(firstPart);
      const estateSubdomain =
        session.platformRole !== 'super_admin' ? session.propertyRoles[0]?.subdomain : null;

      if (isOnPlatformAlias && estateSubdomain) {
        const baseDomain = hostParts.slice(1).join('.');
        window.location.href = `${window.location.protocol}//${estateSubdomain}.${baseDomain}${route}`;
        return;
      }

      void navigate(route);

      // Fire a background refetch to replace the seed with the full
      // profile (includes phone, unitName, organisationId, etc.).
      // This runs after navigation so it doesn't block the redirect.
      getMe()
        .then((me) => queryClient.setQueryData(['me'], me))
        .catch(() => {
          /* seed profile is sufficient for routing */
        });
    },
  });

  const isLocked =
    mutation.isError &&
    mutation.error instanceof Object &&
    'code' in mutation.error &&
    (mutation.error as { code?: string }).code === 'ACCOUNT_LOCKED';

  // Reset and start countdown when account is locked
  useEffect(() => {
    if (!isLocked) return;
    setSecondsLeft(LOCKOUT_SECONDS);
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isLocked]);

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');

  if (isLocked) {
    return (
      <div className="flex flex-col items-center gap-4 py-2 text-center">
        <p className="text-sm text-destructive">
          Your account has been temporarily locked. Try again in{' '}
          <span aria-live="polite">
            {mm}:{ss}
          </span>{' '}
          or check your email.
        </p>
        <Button variant="outline" onClick={() => mutation.reset()}>
          Try again
        </Button>
      </div>
    );
  }

  const apiError =
    mutation.isError && mutation.error instanceof Object && 'message' in mutation.error
      ? String((mutation.error as { message: string }).message)
      : null;

  const onSubmit = (values: LoginFormValues) => {
    mutation.mutate(values);
  };

  return (
    <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            disabled={mutation.isPending}
            aria-invalid={!!errors.email}
            {...register('email')}
          />
          {errors.email && (
            <p className="text-sm text-destructive" role="alert">
              {errors.email.message}
            </p>
          )}
        </Field>
        <Field>
          <div className="flex items-center">
            <FieldLabel htmlFor="password">Password</FieldLabel>
            <Link
              to="/forgot-password"
              className="ml-auto text-sm underline-offset-4 hover:underline"
              tabIndex={mutation.isPending ? -1 : 0}
            >
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              disabled={mutation.isPending}
              aria-invalid={!!errors.password}
              {...register('password')}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-foreground hover:text-foreground"
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-destructive" role="alert">
              {errors.password.message}
            </p>
          )}
        </Field>
        {apiError && (
          <p className="text-sm text-destructive text-center" role="alert">
            {apiError}
          </p>
        )}
        <Field>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Signing in…' : 'Sign in'}
          </Button>
        </Field>
      </FieldGroup>
    </form>
  );
}
